import { useMemo } from "react";
import type { ActionSheetActionItem } from "@shared/ui/overlay/action-sheet";
import type { ApiOmpBoAvOperation } from "@src/api/bo";
import type { ConfirmAndPerformBoOperationParams } from "./useBoOperationConfirm";

/** Опции {@link useBoOperationActionItems}. */
export interface UseBoOperationActionItemsOptions<TContext> {
    /** Доступные операции BO, полученные с backend. */
    operations?: ApiOmpBoAvOperation[];
    /** Контекст документа, для которого строятся параметры выполнения операции. */
    context: TContext | null | undefined;
    /** Дополнительный hook перед запуском confirm/perform flow. */
    onBeforePerform?: () => void;
    /** Выполняет confirm и backend mutation. */
    confirmAndPerform: (params: ConfirmAndPerformBoOperationParams) => Promise<void>;
    /**
     * Строит параметры выполнения конкретной операции.
     * @param operation выбранная BO-операция
     * @param context контекст документа
     */
    buildParams: (operation: ApiOmpBoAvOperation, context: TContext) => ConfirmAndPerformBoOperationParams;
}

/**
 * Строит action sheet пункты для доступных BO operations. \
 * BO остаётся источником команд, а не отдельным типом меню.
 */
export function useBoOperationActionItems<TContext>({
    operations,
    context,
    onBeforePerform,
    confirmAndPerform,
    buildParams,
}: UseBoOperationActionItemsOptions<TContext>): ActionSheetActionItem[] {
    return useMemo(() => {
        if (context == null || operations == null) {
            return [];
        }

        return operations.map((operation) => ({
            type: "action",
            id: `bo-operation-${operation.Code}`,
            label: operation.Name,
            onSelect: (actionSheet) => {
                onBeforePerform?.();
                actionSheet.close();
                void confirmAndPerform(buildParams(operation, context));
            },
            shouldCloseOnSelect: false,
        }));
    }, [buildParams, confirmAndPerform, context, onBeforePerform, operations]);
}
